import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Check, Clock, AlertTriangle, Phone, Mail, User, Calendar } from "lucide-react";
import { AccessBlocked } from "../../components/AccessBlocked";
import { fmtDateTime } from "@/lib/dateFormat";

export default function CRMParty360Page() {
  const { partyId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [blocked, setBlocked] = useState("");
  const [completing, setCompleting] = useState(false);
  const [outcome, setOutcome] = useState("");
  const [summary, setSummary] = useState("");
  const [actForm, setActForm] = useState({ type: "note", summary: "", channel: "" });
  const [showActForm, setShowActForm] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/crm/parties/${partyId}`),
      api.get(`/crm/activities/${partyId}`),
    ]).then(([pd, ad]) => {
      setData(pd.data);
      setActivities(ad.data || []);
    }).catch(e => {
      // FX3b item 8 — plain English on a 403, never the permission key
      if (e.response?.status === 403) { setBlocked(e.response?.data?.detail || "forbidden"); return; }
      setErr(e.response?.data?.detail || "Failed to load this party");
    })
      .finally(() => setLoading(false));
  };
  useEffect(load, [partyId]);

  const handleComplete = async () => {
    if (!data?.next_action) return;
    setCompleting(true);
    try {
      await api.post("/crm/next-actions/complete", { next_action_id: data.next_action._id, outcome, summary });
      toast.success("Action completed — next one scheduled");
      setOutcome(""); setSummary("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setCompleting(false);
  };

  const handleAddActivity = async () => {
    try {
      await api.post("/crm/activities", { party_id: partyId, type: actForm.type, summary: actForm.summary, channel: actForm.channel });
      toast.success("Activity logged");
      setShowActForm(false);
      setActForm({ type: "note", summary: "", channel: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/crm/parties/${partyId}`, { status: newStatus });
      toast.success(`Status → ${newStatus}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (blocked) return <div className="p-8"><AccessBlocked detail={blocked} testid="crm-access-blocked" /></div>;
  if (err) return <div className="p-8"><p className="text-zinc-500 text-sm">{err}</p></div>;
  if (loading || !data) return <div className="p-8"><p className="text-zinc-400 text-xs">Loading...</p></div>;

  const { party, contacts, next_action } = data;
  const STATUS_COLORS = { active: "bg-emerald-100 text-emerald-800", nurture: "bg-blue-100 text-blue-800", dormant: "bg-amber-100 text-amber-800", won: "bg-green-100 text-green-800", lost: "bg-red-100 text-red-800", archived: "bg-zinc-100 text-zinc-600" };

  return (
    <div data-testid="crm-party-360">
      <div className="flex items-center gap-3 mb-4">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(-1)}><ArrowLeft size={16} /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="font-heading text-xl font-bold text-zinc-950">{party.name}</h1>
            <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-sm ${STATUS_COLORS[party.status] || "bg-zinc-100"}`}>{party.status}</span>
            <span className="text-[10px] text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded-sm">{party.current_stage_key}</span>
          </div>
          <p className="text-xs text-zinc-500">{party.company_name} · {party.vertical} · {party.city}, {party.country}</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-zinc-500">Health: <span className={`font-bold ${party.health_score >= 70 ? "text-emerald-600" : party.health_score >= 40 ? "text-amber-600" : "text-red-600"}`}>{party.health_score}</span></span>
          {party.expected_value_paise > 0 && <span className="font-mono text-zinc-700">{(party.expected_value_paise / 100).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}</span>}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Left: Next Action + Contacts */}
        <div className="space-y-4">
          {/* Next Action Card */}
          <div className="border border-zinc-200 rounded-sm p-3" data-testid="next-action-card">
            <h3 className="text-xs font-semibold text-zinc-700 mb-2 flex items-center gap-1"><Clock size={12} /> Current Next Action</h3>
            {next_action ? (
              <div>
                <p className="text-sm font-medium">{next_action.action_type?.replace(/_/g, " ")}</p>
                <p className="text-[10px] text-zinc-500">{next_action.channel} · Due: {fmtDateTime(next_action.due_at)}</p>
                <p className={`text-[10px] font-semibold mt-1 ${next_action.status === "overdue" ? "text-red-600" : "text-emerald-600"}`}>{next_action.status}</p>
                <div className="mt-2 space-y-1">
                  <Input placeholder="Outcome..." value={outcome} onChange={e => setOutcome(e.target.value)} className="h-7 text-xs" data-testid="outcome-input" />
                  <Input placeholder="Notes..." value={summary} onChange={e => setSummary(e.target.value)} className="h-7 text-xs" data-testid="summary-input" />
                  <Button size="sm" className="h-7 text-[10px] w-full" disabled={completing} onClick={handleComplete} data-testid="complete-action-btn">
                    <Check size={12} className="mr-1" /> {completing ? "..." : "Complete & Schedule Next"}
                  </Button>
                </div>
              </div>
            ) : <p className="text-[10px] text-zinc-400">No open action</p>}
          </div>

          {/* Contacts */}
          <div className="border border-zinc-200 rounded-sm p-3">
            <h3 className="text-xs font-semibold text-zinc-700 mb-2 flex items-center gap-1"><User size={12} /> Contacts ({contacts.length})</h3>
            {contacts.map(c => (
              <div key={c._id} className="py-1 border-b border-zinc-50 last:border-0">
                <p className="text-xs font-medium">{c.name} {c.is_primary && <span className="text-[8px] bg-blue-100 text-blue-700 px-1 rounded-sm">PRIMARY</span>}</p>
                <p className="text-[10px] text-zinc-400">{c.role} · {c.email} · {c.phone}</p>
              </div>
            ))}
          </div>

          {/* Status Actions */}
          <div className="border border-zinc-200 rounded-sm p-3">
            <h3 className="text-xs font-semibold text-zinc-700 mb-2">Status Actions</h3>
            <div className="flex flex-wrap gap-1">
              {["active", "nurture", "won", "lost", "archived"].filter(s => s !== party.status).map(s => (
                <Button key={s} size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => handleStatusChange(s)}>{s}</Button>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Timeline */}
        <div className="col-span-2 border border-zinc-200 rounded-sm">
          <div className="px-3 py-2 border-b border-zinc-200 flex items-center justify-between">
            <h3 className="text-xs font-semibold text-zinc-700">Timeline</h3>
            <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => setShowActForm(!showActForm)}>+ Log Activity</Button>
          </div>
          {showActForm && (
            <div className="px-3 py-2 border-b border-zinc-100 bg-zinc-50 space-y-1">
              <div className="flex gap-2">
                <select value={actForm.type} onChange={e => setActForm(f => ({ ...f, type: e.target.value }))} className="h-7 text-xs border border-zinc-200 rounded-sm px-1">
                  {["note", "call", "meeting", "email", "whatsapp", "visit", "sample"].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <Input placeholder="Summary..." value={actForm.summary} onChange={e => setActForm(f => ({ ...f, summary: e.target.value }))} className="h-7 text-xs flex-1" />
                <Button size="sm" className="h-7 text-[10px]" onClick={handleAddActivity}>Save</Button>
              </div>
            </div>
          )}
          <div className="max-h-[500px] overflow-y-auto">
            {activities.length === 0 ? <p className="text-center text-zinc-400 text-xs py-8">No activities yet</p> : activities.map(a => (
              <div key={a._id} className="px-3 py-2 border-b border-zinc-50 hover:bg-zinc-50" data-testid={`activity-${a._id}`}>
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${a.type === "call" ? "bg-blue-100 text-blue-700" : a.type === "meeting" ? "bg-purple-100 text-purple-700" : a.type === "email" ? "bg-cyan-100 text-cyan-700" : "bg-zinc-100 text-zinc-600"}`}>{a.type}</span>
                  <span className="text-[10px] text-zinc-400">{fmtDateTime(a.occurred_at)}</span>
                  {a.direction && <span className="text-[10px] text-zinc-300">{a.direction}</span>}
                </div>
                {a.summary && <p className="text-xs text-zinc-700 mt-0.5">{a.summary}</p>}
                {a.outcome && <p className="text-[10px] text-zinc-500 mt-0.5">Outcome: {a.outcome}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
